import { PlanFinding } from '@core/models/plan-finding.model';
import { normalize } from '@core/services/gbe-error-parser/warning-normalizer';
import {
  EntityRef,
  NormalizedWarning
} from '@core/services/gbe-error-parser/warning.model';

export type FindingChangeKind = 'new' | 'changed' | 'gone';

export interface FindingChange {
  kind: FindingChangeKind;
  /** The entry after the round; the old one for a gone entry. */
  entry: NormalizedWarning;
  /** The entry before the round, for a changed one. */
  previous?: NormalizedWarning;
  /** The finding `entry` was read from, whole as it arrived: its payload is what a raw copy carries. */
  finding: PlanFinding;
}

function refKeys(refs: readonly EntityRef[]): string[] {
  return refs.map((ref) => JSON.stringify([ref.relation, ref.entityType, ref.uid])).sort();
}

function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical);
  if (value === null || typeof value !== 'object') return value;
  return Object.keys(value)
    .sort()
    .map((key) => [key, canonical((value as Record<string, unknown>)[key])]);
}

function exactKey(entry: NormalizedWarning): string {
  return JSON.stringify([entry.code, refKeys(entry.refs), canonical(entry.payload)]);
}

/**
 * A subject made of the refs that survive a gesture and the payload fields that tell two
 * violations about them apart. Route-action and route uids are never reused. Moving a stop or a
 * block keeps its uid, even onto another route, while moving a job onto a new stop gets a freshly
 * minted uid. A stop-anchored subject therefore leaves the route out. The entry has a subject only
 * when one of the kept refs has an anchor type: each rule names the type its reports are about, and
 * a site or vehicle anchors only codes reported once per site or vehicle. With no anchors, the
 * payload keys alone name the subject, and a payload holding none of them names nothing.
 */
function subject(
  keep: (ref: EntityRef) => boolean,
  payloadKeys: readonly string[],
  anchors: readonly string[] = ['JOB']
): (entry: NormalizedWarning) => string | null {
  return (entry) => {
    const kept = entry.refs.filter(keep);
    if (anchors.length > 0 && !kept.some((ref) => anchors.includes(ref.entityType))) {
      return null;
    }
    const values = payloadKeys.map((key) => canonical(entry.payload[key] ?? null));
    if (anchors.length === 0 && values.every((value) => value === null)) return null;
    return JSON.stringify([refKeys(kept), values]);
  };
}

function firstOf(
  ...rules: ((entry: NormalizedWarning) => string | null)[]
): (entry: NormalizedWarning) => string | null {
  return (entry) => {
    for (const rule of rules) {
      const found = rule(entry);
      if (found !== null) return found;
    }
    return null;
  };
}

const regulators = (ref: EntityRef): boolean => ref.relation === 'REGULATOR';

const ofTypes =
  (...types: string[]) =>
  (ref: EntityRef): boolean =>
    types.includes(ref.entityType);

const WINDOW_KEYS = ['kind', 'early_or_late', 'oper_kind'];

const windowsRespected = firstOf(
  subject(regulators, WINDOW_KEYS),
  subject(
    (ref) =>
      ref.entityType === 'ROUTE_ACTION' ||
      (ref.relation === 'REGULATOR' && ref.entityType === 'SITE'),
    WINDOW_KEYS,
    ['ROUTE_ACTION']
  )
);

/** Code → the subject an entry is about across a gesture, or `null` when it names none. */
export const FINDING_SUBJECT_RULES: Readonly<
  Record<string, (entry: NormalizedWarning) => string | null>
> = {
  windows_respected_check: windowsRespected,
  soft_windows_respected_check: windowsRespected,
  feasible_window_check: subject(regulators, ['oper_kind']),
  // One report per stop: the vehicle, the site and the jobs picked up there name it. The
  // compartments and capacity resources still holding cargo are what changes, so they stay out.
  must_be_empty_to_pick_check: subject(
    (ref) =>
      ref.relation === 'REGULATOR' ||
      (ref.relation === 'LANDMARK' &&
        (ref.entityType === 'JOB' || ref.entityType === 'SITE')),
    []
  ),
  vehicle_window_in_route_check: subject(
    ofTypes('ROUTE', 'DRIVER', 'VEHICLE'),
    [],
    ['ROUTE']
  ),
  consecutive_wait_duration_check: subject(ofTypes('ROUTE_ACTION'), [], ['ROUTE_ACTION']),
  max_total_wait_duration_respected_check: subject(
    ofTypes('ROUTE_ACTION'),
    ['period'],
    ['ROUTE_ACTION']
  ),
  // The stop where a limit is measured shifts when stops are added, so it stays out.
  work_regulation_pause_check: subject(
    ofTypes('ROUTE', 'DRIVER', 'WORK_REGULATION'),
    ['limit', 'checkpoint'],
    ['ROUTE']
  ),
  prescribed_route_timing_inadmissible: subject(ofTypes('ROUTE'), [], ['ROUTE']),
  trailer_capacities_check: subject(
    ofTypes('ROUTE_ACTION', 'TRAILER', 'CAPACITY_RESOURCE'),
    [],
    ['ROUTE_ACTION']
  ),
  capacity_coverage_check: subject(ofTypes('JOB', 'SITE', 'CAPACITY_RESOURCE'), [
    'oper_kind'
  ]),
  temperature_respected_check: subject(ofTypes('JOB', 'TRAILER'), ['operation_kind']),
  single_vehicle_window_check: subject(ofTypes('VEHICLE', 'DRIVER'), [], ['VEHICLE']),
  rotation_shape_respected_check: subject(
    ofTypes('ROUTE', 'VEHICLE'),
    ['constraint'],
    ['ROUTE']
  ),
  route_chain_overlap_check: subject(() => false, ['route_uid', 'next_route_uid'], []),
  chronological_order_check: subject(ofTypes('ROUTE'), [], ['ROUTE'])
};

function ruleKey(entry: NormalizedWarning): string | null {
  const subjectOf = FINDING_SUBJECT_RULES[entry.code]?.(entry) ?? null;
  return subjectOf === null ? null : JSON.stringify([entry.code, subjectOf]);
}

function groupBy(
  entries: readonly NormalizedWarning[],
  key: (entry: NormalizedWarning) => string | null
): Map<string, number[]> {
  const groups = new Map<string, number[]>();
  entries.forEach((entry, index) => {
    const k = key(entry);
    if (k === null) return;
    const group = groups.get(k);
    if (group) group.push(index);
    else groups.set(k, [index]);
  });
  return groups;
}

function unpaired(
  before: readonly NormalizedWarning[],
  after: readonly NormalizedWarning[]
): { before: NormalizedWarning[]; after: NormalizedWarning[] } {
  const pool = groupBy(before, exactKey);
  const pairedBefore = new Set<number>();
  const leftAfter = after.filter((entry) => {
    const index = pool.get(exactKey(entry))?.pop();
    if (index === undefined) return true;
    pairedBefore.add(index);
    return false;
  });
  return { before: before.filter((_, i) => !pairedBefore.has(i)), after: leftAfter };
}

function citesTouchedRoute(
  entry: NormalizedWarning,
  touched: ReadonlySet<string>
): boolean {
  return entry.refs.some(
    (ref) => ref.entityType === 'ROUTE' && ref.uid !== null && touched.has(ref.uid)
  );
}

/**
 * Compares the findings before and after an edit round. Equal entries pair first, as a multiset;
 * of the rest, a ruled code's entries pair as changed when their subject has exactly one entry on
 * each side. Everything else is new or gone.
 */
export function diffFindings(
  before: PlanFinding[],
  after: PlanFinding[],
  touchedRouteUids: readonly string[]
): FindingChange[] {
  const findingOf = new Map<NormalizedWarning, PlanFinding>();
  // `normalize`'s second parameter is a nesting depth, so it must not receive flatMap's index.
  const entriesOf = (findings: PlanFinding[]) =>
    findings.flatMap((finding) =>
      normalize(finding).map((entry) => {
        findingOf.set(entry, finding);
        return entry;
      })
    );
  const left = unpaired(entriesOf(before), entriesOf(after));
  const change = (
    kind: FindingChangeKind,
    entry: NormalizedWarning,
    previous?: NormalizedWarning
  ): FindingChange => ({
    kind,
    entry,
    ...(previous ? { previous } : {}),
    finding: findingOf.get(entry) as PlanFinding
  });

  const beforeBySubject = groupBy(left.before, ruleKey);
  const afterBySubject = groupBy(left.after, ruleKey);
  const pairedBefore = new Set<number>();
  const added: FindingChange[] = [];
  const changed: FindingChange[] = [];

  for (const entry of left.after) {
    const key = ruleKey(entry);
    const previous = key === null ? undefined : beforeBySubject.get(key);
    if (key !== null && previous?.length === 1 && afterBySubject.get(key)?.length === 1) {
      pairedBefore.add(previous[0]);
      changed.push(change('changed', entry, left.before[previous[0]]));
    } else {
      added.push(change('new', entry));
    }
  }
  const gone: FindingChange[] = left.before
    .filter((_, i) => !pairedBefore.has(i))
    .map((entry) => change('gone', entry));

  const ordered = [...added, ...changed, ...gone];
  const touched = new Set(touchedRouteUids);
  return [
    ...ordered.filter((change) => citesTouchedRoute(change.entry, touched)),
    ...ordered.filter((change) => !citesTouchedRoute(change.entry, touched))
  ];
}
